import { ReactNode, RefObject, useEffect, useState } from 'react';
import clsx from 'clsx';
import { HiOutlineCog6Tooth } from 'react-icons/hi2';
import { DataTableWrapMode } from '../DataTable.types';
import DataTableSettingsMenu from './DataTableSettingsMenu';

interface IDataTableSettingsControlProps {
    wrapMode: DataTableWrapMode;
    onWrapModeChange: (mode: DataTableWrapMode) => void;
    extra: ReactNode;
    /** Список строк и скролл таблицы: по ним меню считает, сколько строк на экране */
    rowsRef: RefObject<HTMLElement | null>;
    scrollRef: RefObject<HTMLElement | null>;
    internalScroll: boolean;
    rowCount: number;
}

/** Шестерёнка «Настройки таблицы» со своим состоянием: открытие и подсчёт строк на экране не перерисовывают таблицу. */
const DataTableSettingsControl = ({
    wrapMode,
    onWrapModeChange,
    extra,
    rowsRef,
    scrollRef,
    internalScroll,
    rowCount,
}: IDataTableSettingsControlProps) => {
    const [opened, setOpened] = useState(false);

    // Сколько строк помещается в списке при текущей высоте строки: цену выбора
    // показывает меню, поэтому меряем только пока оно открыто.
    const [rowsOnScreen, setRowsOnScreen] = useState<number>();
    // extra у потребителя обычно новый на каждый рендер, а замеру важно только, есть ли он
    const hasExtra = !!extra;
    useEffect(() => {
        if (!opened || !hasExtra) return;
        const list = rowsRef.current;
        const items = list ? (Array.from(list.children) as HTMLElement[]) : [];
        if (!items.length) {
            setRowsOnScreen(undefined);
            return;
        }
        const sample = items.slice(0, 20);
        const avgRow = sample.reduce((sum, el) => sum + el.getBoundingClientRect().height, 0) / sample.length;
        // внутренний скролл знает свою высоту сам; при внешнем считаем от верха
        // строк до низа окна — столько увидит тот, кто пролистал список к началу
        const available = internalScroll
            ? (scrollRef.current?.clientHeight ?? 0)
            : Math.max(0, window.innerHeight - list!.getBoundingClientRect().top);
        setRowsOnScreen(avgRow > 0 ? Math.max(1, Math.floor(available / avgRow)) : undefined);
    }, [opened, hasExtra, wrapMode, internalScroll, rowCount, rowsRef, scrollRef]);

    return (
        <DataTableSettingsMenu
            opened={opened}
            onOpenChange={setOpened}
            wrapMode={wrapMode}
            onWrapModeChange={onWrapModeChange}
            extra={extra}
            rowsOnScreen={rowsOnScreen}
            target={
                <button
                    type="button"
                    className={clsx('dtb__icon-btn', opened && 'is-active')}
                    title="Настройки таблицы"
                    onClick={() => setOpened((v) => !v)}
                >
                    <HiOutlineCog6Tooth aria-hidden />
                </button>
            }
        />
    );
};

export default DataTableSettingsControl;
